import React, { useEffect, useState, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { DaButton, ButtonGroup, CheckIcon } from '@paygreen/paygreen-ui';
import { useAuth } from '../../context';
import { CardClassic, MainLogin } from '../../components';
import SubscriptionForm from './SubscriptionForm';
import SubscriptionSuccessMessage from './SubscriptionSuccessMessage';
import DuplicateAccountMessage from './DuplicateAccountMessage';
import { postRegistrationAssociation } from './service';

const SubscriptionCharity = ({ t }) => {
    const { checkLocalTokens } = useAuth();

    const pathname = useLocation().pathname;
    const isCharitySubscription = pathname === '/subscription-charity';

    const [errorMessage, setErrorMessage] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoadingForm, setIsLoadingForm] = useState(false);
    const [newCharity, setNewCharity] = useState({});

    // initializing all react-hook-form values and methods
    const defaultValues = {
        lastname: '',
        firstname: '',
        password: '',
        passwordVerify: '',
        email: '',
        phone: '',
        associationName: '',
        website: '',
        address: '',
        country: 'FR',
        city: '',
        zipCode: '',
        cgu: '',
    };

    const { handleSubmit, register, errors, setValue, watch, control } =
        useForm({
            defaultValues: defaultValues,
            mode: 'onTouched',
            reValidateMode: 'onChange',
        });

    // creating a special ref for password input to compare password and passwordVerify inputs
    const password = useRef({});
    password.current = watch('password', '');

    useEffect(() => {
        register({ name: 'associationName' });
        register({ name: 'website' });
        register({ name: 'lastname' });
        register({ name: 'firstname' });
        register({ name: 'email' });
        register({ name: 'phone' });
        register({ name: 'password' });
        register({ name: 'passwordVerify' });
        register({ name: 'country' });
        register({ name: 'address' });
        register({ name: 'city' });
        register({ name: 'zipCode' });
        register({ name: 'cgu' });
    }, [register]);

    /**
     * Post request to API for new association's subscription
     * @param {object} newAssociation - An object containing all data of the new association
     * @returns {object} - Object with all infos from new association
     */
    const associationSubscription = (newAssociation) => {
        postRegistrationAssociation(newAssociation)
            .then((res) => {
                setNewCharity(res.data);
                setIsSuccess(true);
            })
            .catch((error) => {
                const { detail, status } = error.response.data;

                if (status === 409) {
                    setErrorMessage(
                        <DuplicateAccountMessage
                            isCharitySubscription={isCharitySubscription}
                        />,
                    );
                } else {
                    setErrorMessage(detail);
                }
            })
            .finally(() => setIsLoadingForm(false));
    };

    const submitForm = (values) => {
        const {
            associationName,
            website,
            representativeLastname,
            firstname,
            phone,
            password,
            email,
            country,
            address,
            city,
            zipCode,
        } = values;

        const finalAssociation = {
            associationName,
            website,
            lastname: representativeLastname,
            firstname,
            phone: phone.replace(/\(0\)|[\s+]/g, ''),
            password,
            email,
            country,
            addressLine1: address,
            addressLine2: '',
            city,
            zipCode,
        };

        setIsLoadingForm(true);
        associationSubscription(finalAssociation);
    };

    if (checkLocalTokens() === 'valid') {
        return <Redirect to="/" />;
    }

    return (
        <MainLogin
            isLoading={isLoadingForm}
            helmetTitle={
                t('subscription.metaTitle') + ' - ' + t('main.metaTitle')
            }
        >
            <CardClassic
                radiusSize="lg"
                blockWidth="md"
                paddingLateral="md"
                paddingTop="md"
                paddingBottom="md"
                titleColorWab="grey30"
                titleAlign="center"
                titleSize="md"
                isShadowWab={true}
                title={isSuccess ? null : t('subscription.charityTitle')}
            >
                {!isSuccess ? (
                    <SubscriptionForm
                        errors={errors}
                        setValue={setValue}
                        register={register}
                        password={password}
                        errorMessage={errorMessage}
                        onSubmit={handleSubmit(submitForm)}
                        isLoadingForm={isLoadingForm}
                        control={control}
                        isCharitySubscription={true}
                    >
                        <ButtonGroup marginTop="xs">
                            <button type="submit">
                                <DaButton
                                    buttonSize="lg"
                                    gradient="theme"
                                    colorTheme="quaternary"
                                    icon={<CheckIcon />}
                                >
                                    {t('subscription.button')}
                                </DaButton>
                            </button>
                        </ButtonGroup>
                    </SubscriptionForm>
                ) : (
                    <SubscriptionSuccessMessage
                        newUser={newCharity}
                        isCharitySubscription={true}
                    />
                )}
            </CardClassic>
        </MainLogin>
    );
};

export default withTranslation('common', 'countries')(SubscriptionCharity);
